import {CropYieldForecast} from '@6grain/masofa-monolith-api-client/ai-inference';
import {Season} from '@6grain/masofa-monolith-api-client/crop-monitoring';
import {ymdFromApiDate} from '@app/pages/electronic-field-map/pages/gis-fields/components/modals/field-details-dialog/managers/field-details-date.util';

export const YIELD_FORECAST_EMPTY = '—';

export interface YieldForecastGeneralModel {
  crop: string;
  variety: string;
  plantingDate: string;
  harvestDate: string;
  harvest: string;
  area: string;
  calcDate: string;
  pessimistic: string;
  likely: string;
  optimistic: string;
  title: string;
}

export interface YieldForecastGeneralInput {
  forecast: CropYieldForecast | null;
  cropName: string | null | undefined;
  varietyName: string | null | undefined;
  fieldName: string | null | undefined;
  season: Season | null;
  formatNumber?: (value: number) => string;
}

/** First finite number, including 0. Null and non-finite values fall through. */
export function coalesceNumber(...values: (number | null | undefined)[]): number | null {
  for (const value of values) {
    if (value != null && Number.isFinite(Number(value))) {
      return Number(value);
    }
  }
  return null;
}

export function formatDisplayDate(raw: string | null | undefined, withTime = false): string {
  if (!raw) {
    return YIELD_FORECAST_EMPTY;
  }
  const ymd = ymdFromApiDate(raw);
  if (!ymd) {
    return YIELD_FORECAST_EMPTY;
  }
  const [year, month, day] = ymd.split('-');
  const date = `${day}.${month}.${year}`;
  if (!withTime) {
    return date;
  }
  const time = raw.match(/T(\d{2}):(\d{2})/);
  return time ? `${date} ${time[1]}:${time[2]}` : date;
}

export function formatCalcDate(raw: string | null | undefined): string {
  if (!raw) {
    return YIELD_FORECAST_EMPTY;
  }
  return formatDisplayDate(raw, raw.includes('T'));
}

export function buildYieldForecastGeneral(input: YieldForecastGeneralInput): YieldForecastGeneralModel {
  const season = input.season;
  const forecast = input.forecast;
  const format = input.formatNumber ?? ((value: number) => value.toFixed(2));
  const plantingRaw = season?.plantingDate || season?.plantingDatePlan || null;
  const harvestRaw = season?.harvestingDate || season?.harvestingDatePlan || null;
  const plantingDate = formatDisplayDate(plantingRaw);
  const harvestDate = formatDisplayDate(harvestRaw);
  const calcRaw = forecast?.forecastDate || season?.yieldForecastCalculationDate || null;

  return {
    crop: textOrEmpty(input.cropName),
    variety: textOrEmpty(input.varietyName),
    plantingDate,
    harvestDate,
    harvest: formatAmount(coalesceNumber(forecast?.harvest, forecast?.forecastAbsolute), format),
    area: formatAmount(coalesceNumber(forecast?.area, season?.fieldArea), format),
    calcDate: formatCalcDate(calcRaw),
    pessimistic: formatAmount(coalesceNumber(season?.yieldHaPessimistic), format),
    likely: formatAmount(coalesceNumber(season?.yieldHaPlan), format),
    optimistic: formatAmount(coalesceNumber(season?.yieldHaOptimistic), format),
    title: textOrEmpty(input.fieldName),
  };
}

function formatAmount(value: number | null, format: (value: number) => string): string {
  return value == null ? YIELD_FORECAST_EMPTY : format(value);
}

function textOrEmpty(value: string | null | undefined): string {
  const text = value?.trim();
  return text ? text : YIELD_FORECAST_EMPTY;
}
